import { useState } from 'react';

import { useParams } from '@tanstack/react-router';

import type { SpeakerLabels } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';

import { updateTranscriptionSpeakerLabels } from '../../../ducks/asset_transcriptions/thunks';
import { ICONS } from '../../../icons/icon';
import { selectTranscriptionPartsAndSpeakerLabels } from '../../../panels/asset_transcriptions/selectors';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import Button from '../../../ui/button/button';
import ControlsModal from '../../../ui/controls_modal/controls_modal';
import TextFieldWrapper from '../../../ui/forms/field_wrappers/text/text';
import type { ModalComponentProps } from '../modals_manager/types';

import styles from './transcriptions_edit_speakers.module.css';

type Speakers = {
  speakers: SpeakerLabels;
};

export function TranscriptionSpeakersModal({
  isOpen,
  onCancel,
  onConfirm,
}: ModalComponentProps) {
  const dispatch = useAppDispatch();
  const { id: assetId } = useParams({ strict: false });
  const [speakerLabels, setSpeakerLabels] = useState<SpeakerLabels>({});

  const { speakerParts, speakerLabels: savedSpeakerLabels } = useAppSelector(
    (state) => selectTranscriptionPartsAndSpeakerLabels(state, assetId),
  );

  const speakersInUse = new Set(
    speakerParts
      .map(({ speaker }) => speaker)
      .filter((speaker): speaker is number => speaker != null),
  );

  const handleConfirm = (data: Speakers) => {
    if (!assetId || !data.speakers) {
      return Promise.resolve();
    }
    // Hack to pick only those which are in the state
    const updatedSpeakers = Object.fromEntries(
      Object.entries(data.speakers).filter(([key]) =>
        Object.hasOwn(speakerLabels, key),
      ),
    );

    return dispatch(
      updateTranscriptionSpeakerLabels(assetId, updatedSpeakers),
    ).then(onConfirm);
  };

  const addSpeaker = () => {
    const speakerIds = Object.keys(speakerLabels).map((key) =>
      parseInt(key, 10),
    );
    const newSpeakerId = speakerIds.length ? Math.max(...speakerIds) + 1 : 0;

    setSpeakerLabels({
      ...speakerLabels,
      [newSpeakerId]: `Speaker ${newSpeakerId + 1}`,
    });
  };

  const removeSpeaker = (speakerId: number) => {
    const { [speakerId]: removedSpeaker, ...remainingSpeakers } = speakerLabels;

    setSpeakerLabels(remainingSpeakers);
  };

  return (
    <ControlsModal<Speakers>
      headerText="Manage Speakers"
      confirmButtonText="Save"
      variant="success"
      isOpen={isOpen}
      // Copy speakerLabels from the store to state when opening modal window
      onOpen={() => {
        setSpeakerLabels({ ...savedSpeakerLabels });
      }}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      isForm
    >
      {Object.keys(speakerLabels).map((key) => {
        const speakerId = parseInt(key, 10);
        return (
          <div className={styles.speaker_row} key={key}>
            <TextFieldWrapper
              className={styles.speaker_field}
              name={`speakers.${key}`}
              value={speakerLabels[speakerId]}
              label={`Speaker ${speakerId + 1}`}
              required
            />
            <Button
              onClick={() => removeSpeaker(speakerId)}
              icon={ICONS.TrashBin}
              wideBasis={false}
              variant="danger"
              type="button"
              disabled={speakersInUse.has(speakerId)}
              aria={{ label: `Remove speaker ${speakerId + 1}` }}
              noBorder
            />
          </div>
        );
      })}

      <div className={styles.actions}>
        <Button onClick={addSpeaker} icon={ICONS.Plus} noBorder>
          Add speaker
        </Button>
      </div>
    </ControlsModal>
  );
}
